import { $axiosManager } from '@/api/$axios';

const SERVICE_PATH = '/user-settings';

// $axiosManager deep-transforms request/response JSON keys (camelCase ↔ snake_case).
// A user-setting `value` is an OPAQUE client blob — e.g. the OLAP view-state envelope
// whose keys include `_v`, `_schemaVersion` and snake_case colId filter keys. Letting
// the interceptor rewrite those keys corrupts the blob (`_v`→`V`, which breaks version
// detection in unwrapSessionState → progressive double-wrapping; the WP-602 regression).
// Serializing `value` to a JSON string makes it a leaf the interceptor leaves untouched,
// so it round-trips byte-for-byte. The backend types `value` as anyOf<object|string>
// and echoes it back verbatim.
const serializeValue = (value) =>
  value === undefined || typeof value === 'string' ? value : JSON.stringify(value);

// Inverse on read. New rows are JSON strings → parse them back to objects. Legacy rows
// are objects (already key-transformed by the interceptor before the fix) → left as-is;
// the OLAP loader heals those separately (unwrapSessionState discards corrupted envelopes).
const parseValue = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const parseItemsInResponse = (res) => {
  const items = res?.data?.items;
  if (Array.isArray(items)) {
    items.forEach((item) => {
      if (item && 'value' in item) item.value = parseValue(item.value);
    });
  }
  return res;
};

/**
 * Upsert (create or update) a user setting.
 *
 * @param {{ page_name: string, key?: string, value: object|string, country_code?: string, is_personal?: boolean }} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const upsertUserSetting = (payload) =>
  $axiosManager.post(SERVICE_PATH, {
    key: 'default',
    is_personal: true,
    ...payload,
    value: serializeValue(payload.value),
  });

/**
 * Get all user settings, optionally filtered.
 *
 * @param {{ page_name?: string, key?: string, country_code?: string }} [params]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getUserSettings = (params = {}) =>
  $axiosManager.get(SERVICE_PATH, { params }).then(parseItemsInResponse);

/**
 * Get settings for a specific page.
 *
 * @param {string} pageName
 * @param {{ key?: string, country_code?: string }} [params]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getUserSettingsByPage = (pageName, params = {}) =>
  $axiosManager.get(`${SERVICE_PATH}/${pageName}`, { params }).then(parseItemsInResponse);
